import { filter, forEach, map, reduce } from 'lodash';
import globalRelay from '../globalRelay';
import enums from '../enum';
import util from '../util';

/* item types:
	'builder-upgrade',
	'cabinet',
	'client-upgrade',
	'delivery',
	'design-retainer',
	'glass',
	'installation',
	'installation-custom',
	'labour',
	'misc',
	'outside-supplier',
	'referral-fee',
	'rubbish-removal',
	'service',
	'solid-surface-counter',
	'special-order-glass',
	'subtrade',
	'tax',
	'warehouse',
	'warranty'
*/

// non-upgrade items included in "order" calculations
const _orderTypes = [
	'cabinet',
	'delivery',
	'glass',
	'installation',
	'installation-custom',
	'labour',
	'misc',
	'outside-supplier',
	'rubbish-removal',
	'service',
	'special-order-glass',
	'travel',
	'warehouse'];

const _subtradeTypes = ['subtrade'];
const _solidSurfaceCounterTypes = ['solid-surface-counter'];
const findRoot = item => { return item.parent ? findRoot(item.parent) : item; };

// NOTE: detail items have the shape { cost: 0.00, description: '', group: ('order', 'subtrade', 'solid-surface-counter'), isOptional: true, sellPrice: 0.00, type: '' }
class JobCostSummary {
	_jobCost;
	_appliedPaymentTotal;
	_detailItems;
	_extensionItems;
	_cachedValues;

	constructor (jobCost, appliedPaymentTotal) {
		this._jobCost = jobCost;
		this._appliedPaymentTotal = appliedPaymentTotal || 0;
		this._detailItems = [];
		this._extensionItems = [];
		this._resetCachedValues();
	}
	get jobCost () {
		return this._jobCost;
	}
	get appliedPaymentTotal () {
		return this.getStatValue('appliedPaymentTotal', this._appliedPaymentTotal);
	}
	get builderDiscountPercent () {
		const builderDiscountPercent = this.jobCost.builderDiscountPercent || 0;

		return this.getStatValue('builderDiscountPercent', builderDiscountPercent);
	}
	get orderCabinetCost () {
		return this._getCachedValue('orderCabinetCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'cabinet');

			return this.calculateTotalCost('order', ['cabinet'], taxCost);
		});
	}
	get orderCabinetSell () {
		return this._getCachedValue('orderCabinetSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'cabinet');

			return this.calculateTotalSell('order', ['cabinet'], taxSell);
		});
	}
	get orderCabinetFreightCost () {
		return this._getCachedValue('orderCabinetFreightCost', () => {
			return util.roundAsCurrency(reduce(this.getItemsByGroupAndType('order', ['cabinet']), (result, item) => {
				return result + util.roundAsCurrency(item.freight);
			}, 0));
		});
	}
	get orderCabinetFreightSell () {
		return this._getCachedValue('orderCabinetFreightSell', () => {
			return util.roundAsCurrency(reduce(this.getItemsByGroupAndType('order', ['cabinet']), (result, item) => {
				return result + util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(item.freight, item.margin));
			}, 0));
		});
	}
	get orderOutsideSupplierCost () {
		return this._getCachedValue('orderOutsideSupplierCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'outside-supplier');

			return this.calculateTotalCost('order', ['outside-supplier'], taxCost);
		});
	}
	get orderOutsideSupplierSell () {
		return this._getCachedValue('orderOutsideSupplierSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'outside-supplier');

			return this.calculateTotalSell('order', ['outside-supplier'], taxSell);
		});
	}
	get orderWarehouseCost () {
		return this._getCachedValue('orderWarehouseCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'warehouse');

			return this.calculateTotalCost('order', ['warehouse'], taxCost);
		});
	}
	get orderWarehouseSell () {
		return this._getCachedValue('orderWarehouseSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'warehouse');

			return this.calculateTotalSell('order', ['warehouse'], taxSell);
		});
	}
	get orderMiscCost () {
		return this._getCachedValue('orderMiscCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'misc');

			return this.calculateTotalCost('order', ['misc'], taxCost);
		});
	}
	get orderMiscSell () {
		return this._getCachedValue('orderMiscSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'misc');

			return this.calculateTotalSell('order', ['misc'], taxSell);
		});
	}
	get orderAccessoryModCost () {
		return this.orderOutsideSupplierCost + this.orderWarehouseCost + this.orderMiscCost;
	}
	get orderAccessoryModSell () {
		return this.orderOutsideSupplierSell + this.orderWarehouseSell + this.orderMiscSell;
	}
	get orderGlassCost () {
		return this._getCachedValue('orderGlassCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'glass');

			return this.calculateTotalCost('order', ['glass'], taxCost);
		});
	}
	get orderGlassSell () {
		return this._getCachedValue('orderGlassSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'glass');

			return this.calculateTotalSell('order', ['glass'], taxSell);
		});
	}
	get orderSpecialGlassCost () {
		return this._getCachedValue('orderSpecialGlassCost', () => {
			const taxCost = this.calculatePreSubtotalTaxCost('order', 'special-order-glass');

			return this.calculateTotalCost('order', ['special-order-glass'], taxCost);
		});
	}
	get orderSpecialGlassSell () {
		return this._getCachedValue('orderSpecialGlassSell', () => {
			const taxSell = this.calculatePreSubtotalTaxSell('order', 'special-order-glass');

			return this.calculateTotalSell('order', ['special-order-glass'], taxSell);
		});
	}
	get orderInstallationCost () {
		return this._getCachedValue('orderInstallationCost', () => {
			return this.calculateTotalCost('order', ['installation', 'installation-custom'], 0);
		});
	}
	get orderInstallationSell () {
		return this._getCachedValue('orderInstallationSell', () => {
			return this.calculateTotalSell('order', ['installation', 'installation-custom'], 0);
		});
	}
	get orderInstallServiceCost () {
		return this._getCachedValue('orderInstallServiceCost', () => {
			return this.calculateTotalCost('order', ['service'], 0);
		});
	}
	get orderInstallServiceSell () {
		return this._getCachedValue('orderInstallServiceSell', () => {
			return this.calculateTotalSell('order', ['service'], 0);
		});
	}
	get orderTravelCost () {
		return this._getCachedValue('orderTravelCost', () => {
			return this.calculateTotalCost('order', ['travel'], 0);
		});
	}
	get orderTravelSell () {
		return this._getCachedValue('orderTravelSell', () => {
			return this.calculateTotalSell('order', ['travel'], 0);
		});
	}
	get orderReferralFeeCost () {
		return this._getCachedValue('orderReferralFeeCost', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['referral-fee'].indexOf(item.type) !== -1 && item.isPreSubtotal && item.group === 'order'), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, 0));
		});
	}
	get orderReferralFeePostSubtotalCost () {
		return this._getCachedValue('orderReferralFeePostSubtotalCost', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal && item.group === 'order'), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, 0));
		});
	}
	get orderDeliveryCost () {
		return this._getCachedValue('orderDeliveryCost', () => {
			return this.calculateTotalCost('order', ['delivery'], 0);
		});
	}
	get orderDeliverySell () {
		return this._getCachedValue('orderDeliverySell', () => {
			return this.calculateTotalSell('order', ['delivery'], 0);
		});
	}
	get clientUpgradeSellPrice () {
		return this._getCachedValue('clientUpgradeSellPrice', () => {
			const items = filter(this.getItemsByGroupAndType('order', ['client-upgrade']), item => !item.isChargedToBuilder);

			const result = util.roundAsCurrency(reduce(items, (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return this.getStatValue('clientUpgradeSellPrice', result);
		});
	}
	get clientUpgradeChargedToBuilderSellPrice () {
		return this._getCachedValue('orderClientUpgradeChargedToBuilderSellPrice', () => {
			const items = filter(this.getItemsByGroupAndType('order', ['client-upgrade']), item => item.isChargedToBuilder);

			return util.roundAsCurrency(reduce(items, (result, item) => {
				return result + util.roundAsCurrency(item.sellPrice);
			}, 0));
		});
	}
	get builderUpgradeSellPrice () {
		return this._getCachedValue('builderUpgradeSellPrice', () => {
			const items = this.getItemsByGroupAndType('order', ['builder-upgrade']);

			let result = util.roundAsCurrency(reduce(items, (val, item) => {
				return val + util.roundAsCurrency(item.sellPrice);
			}, 0));

			result += (this.clientUpgradeChargedToBuilderSellPrice * (1 - this.builderDiscountPercent));

			return this.getStatValue('builderUpgradeSellPrice', result);
		});
	}
	// get builderSubtotal () {
	// 	return this._getCachedValue('builderSubtotal', () => {
	// 		return this.builderUpgradeSellPrice + (this.orderSellPrice * (1 - this.jobCost.builderDiscountPercent));
	// 	});
	// }
	get orderTotalCost () {
		return this._getCachedValue('orderTotalCost', () => {
			const total = this.calculateTotalCost('order', _orderTypes, 0);

			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['calculated-fee', 'tax', 'warranty', 'referral-fee'].indexOf(item.type) !== -1 && item.isPreSubtotal && item.group === 'order'), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, total));
		});
	}
	get orderCaculatedSellPrice () {
		return this._getCachedValue('orderCaculatedSellPrice', () => {
			const total = this.calculateTotalSell('order', _orderTypes, 0);

			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['calculated-fee', 'tax', 'warranty', 'referral-fee'].indexOf(item.type) !== -1 && item.isPreSubtotal && item.group === 'order'), (result, item) => {
				return result + util.roundAsCurrency(item.sellPrice);
			}, total));
		});
	}
	get orderSellPrice () {
		const adjustedSellPrice = (this._jobCost.currentChangeOrder) ? this._jobCost.currentChangeOrder.adjustedSellPrice : this._jobCost.adjustedSellPrice;
		const result = (typeof adjustedSellPrice === 'undefined') ? this.orderCaculatedSellPrice : adjustedSellPrice;

		return this.getStatValue('orderSellPrice', result);
	}
	get orderTotalSellWithUpgrades () {
		const result = this.orderSellPrice + this.builderUpgradeSellPrice + this.clientUpgradeSellPrice;

		return this.getStatValue('orderTotalSellWithUpgrades', result);
	}
	get orderNetPrice () {
		return (this.orderSellPrice + this.orderFees) - this.appliedPaymentTotal;
	}
	get orderTaxes () {
		return this._getCachedValue('orderTaxes', () => {
			const result = util.roundAsCurrency(reduce(filter(this._detailItems, item => item.group === 'order' && ['tax'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return result;
		});
	}
	get orderFees () {
		return this._getCachedValue('orderFees', () => {
			const result = util.roundAsCurrency(reduce(filter(this._detailItems, item => item.group === 'order' && ['warranty', 'calculated-fee', 'referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return result;
		});
	}
	get orderTaxesAndFees () {
		return this.orderTaxes + this.orderFees;
	}
	get clientUpgradeTaxesAndFees () {
		return this._getCachedValue('clientUpgradeTaxesAndFees', () => {
			const result = util.roundAsCurrency(reduce(filter(this._detailItems, item => item.group === 'client-upgrade' && ['warranty', 'tax', 'calculated-fee', 'referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return result;
		});
	}
	get orderTotalPrice () {
		return this._getCachedValue('orderTotalPrice', () => {
			const result = this.orderNetPrice + this.orderTaxes + this.builderUpgradeSellPrice;

			return this.getStatValue('orderTotalPrice', result);
		});
	}
	get subtradeCost () {
		return this._getCachedValue('subtradeCost', () => {
			return this.calculateTotalCost('subtrade', _subtradeTypes, 0);
		});
	}
	get subtradeSellPrice () {
		return this._getCachedValue('subtradeSellPrice', () => {
			return this.calculateTotalSell('subtrade', _subtradeTypes, 0);
		});
	}
	get subtradeTotalCost () {
		return this._getCachedValue('subtradeTotalCost', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty'].indexOf(item.type) !== -1 && item.group === 'subtrade'), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, this.subtradeCost));
		});
	}
	get subtradeTotalSellPrice () {
		return this._getCachedValue('subtradeTotalSellPrice', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty'].indexOf(item.type) !== -1 && item.group === 'subtrade'), (result, item) => {
				return result + util.roundAsCurrency(item.sellPrice);
			}, this.subtradeSellPrice));
		});
	}
	get subtradeTaxesAndFees () {
		return this._getCachedValue('subtradeTaxesAndFees', () => {
			const result = util.roundAsCurrency(reduce(filter(this._detailItems, item => item.group === 'subtrade' && ['tax', 'calculated-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return result;
		});
	}
	get subtradeTotalPrice () {
		return this._getCachedValue('subtradeTotalPrice', () => {
			const result = this.subtradeTotalSellPrice + this.subtradeTaxesAndFees;

			return this.getStatValue('subtradeTotalPrice', result);
		});
	}
	get solidSurfaceCounterCost () {
		return this._getCachedValue('solidSurfaceCounterCost', () => {
			return this.calculateTotalCost('solid-surface-counter', _solidSurfaceCounterTypes, 0);
		});
	}
	get solidSurfaceCounterSellPrice () {
		return this._getCachedValue('solidSurfaceCounterSellPrice', () => {
			return this.calculateTotalSell('solid-surface-counter', _solidSurfaceCounterTypes, 0);
		});
	}
	get solidSurfaceCounterTotalCost () {
		return this._getCachedValue('solidSurfaceCounterTotalCost', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty'].indexOf(item.type) !== -1 && item.group === 'solid-surface-counter'), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, this.solidSurfaceCounterCost));
		});
	}
	get solidSurfaceCounterTotalSellPrice () {
		return this._getCachedValue('solidSurfaceCounterTotalSellPrice', () => {
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty'].indexOf(item.type) !== -1 && item.group === 'solid-surface-counter'), (result, item) => {
				return result + util.roundAsCurrency(item.sellPrice);
			}, this.solidSurfaceCounterSellPrice));
		});
	}
	get solidSurfaceCounterTaxesAndFees () {
		return this._getCachedValue('solidSurfaceCounterTaxesAndFees', () => {
			const result = util.roundAsCurrency(reduce(filter(this._detailItems, item => item.group === 'solid-surface-counter' && ['tax', 'calculated-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
				return total + util.roundAsCurrency(item.sellPrice);
			}, 0));

			return result;
		});
	}
	get solidSurfaceCounterTotalPrice () {
		return this._getCachedValue('solidSurfaceCounterTotalPrice', () => {
			const result = this.solidSurfaceCounterTotalSellPrice + this.solidSurfaceCounterTaxesAndFees;

			return this.getStatValue('solidSurfaceCounterTotalPrice', result);
		});
	}
	// get grandTotalCoreCost () {
	// 	return this._getCachedValue('grandTotalCoreCost', () => {
	// 		return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty', 'tax', 'client-upgrade'].indexOf(item.type) === -1), (result, item) => {
	// 			return result + util.roundAsCurrency(item.cost);
	// 		}, 0));
	// 	});
	// }
	get grandTotalCost () {
		return this._getCachedValue('grandTotalCost', () => {
			const baseCost = this.subtradeTotalCost + this.solidSurfaceCounterTotalCost + this.orderTotalCost;

			// add in the "non-margined" referral fees
			return util.roundAsCurrency(reduce(filter(this._detailItems, item => ['referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (result, item) => {
				return result + util.roundAsCurrency(item.cost);
			}, baseCost));
		});
	}
	get grandTotalSell () {
		return this._getCachedValue('grandTotalSell', () => {
			let result;
			if (this.jobCost.currentChangeOrder && this.jobCost.currentChangeOrder.type === 'billing-adjustment') {
				result = this.jobCost.currentChangeOrder.adjustedSellPrice;
			}
			else {
				const baseSell = (this.orderTotalSellWithUpgrades + this.subtradeTotalSellPrice + this.solidSurfaceCounterTotalSellPrice);
				result = util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty', 'referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
					return total + util.roundAsCurrency(item.sellPrice);
				}, baseSell));
			}

			return this.getStatValue('grandTotalSell', result);
		});
	}
	get grandTotalContract () {
		return this._getCachedValue('grandTotalContract', () => {
			let result;

			if (this.jobCost.currentChangeOrder && this.jobCost.currentChangeOrder.type === 'billing-adjustment') {
				result = this.jobCost.currentChangeOrder.adjustedSellPrice;
			}
			else {
				const baseSell = (this.orderTotalSellWithUpgrades + this.subtradeTotalSellPrice + this.solidSurfaceCounterTotalSellPrice);
				result = util.roundAsCurrency(reduce(filter(this._detailItems, item => ['warranty', 'tax', 'referral-fee'].indexOf(item.type) !== -1 && !item.isPreSubtotal), (total, item) => {
					return total + util.roundAsCurrency(item.sellPrice);
				}, baseSell));
			}

			return this.getStatValue('grandTotalContract', result);
		});
	}
	get orderWarrantyItems () {
		return this.getItemsByGroupAndType('order', ['warranty']);
	}
	get orderTaxItems () {
		return this.getItemsByGroupAndType('order', ['tax']);
	}
	get orderCalculatedFeeItems () {
		return this.getItemsByGroupAndType('order', ['calculated-fee']);
	}
	get clientUpgradeTaxItems () {
		return this.getItemsByGroupAndType('client-upgrade', ['tax']);
	}
	get subtradeWarrantyItems () {
		return this.getItemsByGroupAndType('subtrade', ['warranty']);
	}
	get subtradeTaxItems () {
		return this.getItemsByGroupAndType('subtrade', ['tax']);
	}
	get solidSurfaceCounterWarrantyItems () {
		return filter(this._detailItems, item => (item.group === 'solid-surface-counter' && ['warranty'].indexOf(item.type) !== -1));
	}
	get solidSurfaceCounterTaxItems () {
		return filter(this._detailItems, item => (item.group === 'solid-surface-counter' && ['tax'].indexOf(item.type) !== -1));
	}
	get extensionItems () {
		return this._extensionItems;
	}
	buildItems () {
		const jobCost = this.jobCost;
		const changeOrderKey = jobCost.currentChangeOrder && jobCost.currentChangeOrder.key;
		let items = jobCost.lineItems; //this.jobCost.currentChangeOrder ? filter(this.jobCost.lineItems, { changeOrderKey: this.jobCost.currentChangeOrder.key }) : filter(this.jobCost.lineItems, { changeOrderKey: undefined });

		// be sure only to include special items specific to this contract
		items = filter(items, item => ['calculated-fee', 'tax', 'warranty'].indexOf(item.type) === -1 || item.changeOrderKey === changeOrderKey);

		this._detailItems = map(items, item => {
			const group = ['tax', 'warranty', 'calculated-fee'].indexOf(item.type) !== -1 ? item.group : ['subtrade', 'solid-surface-counter'].indexOf(item.type) !== -1 ? item.type : 'order';
			const rootItem = changeOrderKey ? null : findRoot(item);
			const netValues = changeOrderKey ? item.getNetValues(true) : rootItem.getNetValues(true); //{ cost: rootItem.totalCost, freight: rootItem.freight, sell: rootItem.sellPrice };

			return {
				cost: netValues.cost,
				freight: netValues.freight,
				description: item.description,
				group: group,
				isChargedToBuilder: item.isChargedToBuilder,
				isOptional: item.isOptional,
				isPreSubtotal: item.isPreSubtotal,
				sellPrice: netValues.sell,
				relatedType: item.relatedType,
				type: item.type,
				itemNote: item.itemNote,
				changed: item.changed,
				deleted: (item.action === enums.trackableActions.Unset),
				contractorKey: item.contractorKey,
				vendorKey: item.vendorKey
			};
		});

		globalRelay.triggerJobCostBuildSummaryList(this);
	}
	addItem (item) {
		item.isPreSubtotal = !!(item.reportingFlags & enums.jobCostLineItemReportingFlags.Pre_Subtotal);

		this._detailItems.push(item);
		this._resetCachedValues();
	}
	addExtensionItem (item) {
		item.isPreSubtotal = !!(item.reportingFlags & enums.jobCostLineItemReportingFlags.Pre_Subtotal);

		this._extensionItems.push(item);
	}
	getItemsByGroupAndType (group, types) {
		const result = filter(this._detailItems, item => {
			return (item.group === group && types.indexOf(item.type) !== -1);
		});
		return result;
	}
	writeCalculatedItems (jobCost, changeOrderKey) {
		forEach(filter(this._detailItems, item => ['calculated-fee', 'warranty', 'tax'].indexOf(item.type) !== -1), item => {
			item.changeOrderKey = changeOrderKey;
			jobCost.lineItems.add(item);
		});
	}
	eraseCalculatedItems (jobCost, changeOrderKey) {
		forEach(filter(jobCost.lineItems, item => ['calculated-fee', 'warranty', 'tax'].indexOf(item.type) !== -1 && item.changeOrderKey === changeOrderKey), item => {
			jobCost.lineItems.delete(item.key);
		});
	}
	_resetCachedValues () {
		this._cachedValues = {};
	}
	calculateTotalCost (group, types, initalValue?) {
		return util.roundAsCurrency(reduce(this.getItemsByGroupAndType(group, types), (result, item) => {
			return result + util.roundAsCurrency(item.cost);
		}, initalValue || 0));
	}
	calculateTotalSell (group, types, initalValue?) {
		return util.roundAsCurrency(reduce(this.getItemsByGroupAndType(group, types), (result, item) => {
			return result + util.roundAsCurrency(item.sellPrice);
		}, initalValue || 0));
	}
	calculatePreSubtotalTaxCost (group, relatedType) {
		return util.roundAsCurrency(reduce(filter(this.getItemsByGroupAndType(group, ['tax']), { relatedType: relatedType, isPreSubtotal: true }), (result, item) => {
			return result + util.roundAsCurrency(item.cost);
		}, 0));
	}
	calculatePreSubtotalTaxSell (group, relatedType) {
		return util.roundAsCurrency(reduce(filter(this.getItemsByGroupAndType(group, ['tax']), { relatedType: relatedType, isPreSubtotal: true }), (result, item) => {
			return result + util.roundAsCurrency(item.sellPrice);
		}, 0));
	}
	_getCachedValue (key, accessor) {
		let result = this._cachedValues[key];

		if (typeof result === 'undefined') {
			this._cachedValues[key] = result = accessor();
		}

		return result;
	}
	getStatValue (name, defaultValue) {
		const jobCost = this.jobCost;
		const changeOrderKey = jobCost.currentChangeOrder && jobCost.currentChangeOrder.key;

		return jobCost.getStatValue(name, changeOrderKey, defaultValue);
	}

	eraseStats (jobCost, changeOrderKey) {
		const group = enums.jobCostStatGroups.Contract_Summary;

		forEach(filter(jobCost.stats, stat => stat.group === group && stat.changeOrderKey === changeOrderKey), stat => {
			jobCost.stats.delete(stat.key);
		});

		this._resetCachedValues();
	}
	writeStats (jobCost, changeOrderKey, appliedPaymentTotal) {
		const group = enums.jobCostStatGroups.Contract_Summary;
		const stats:any[] = [];

		this.eraseStats(jobCost, changeOrderKey);

		if (typeof appliedPaymentTotal !== 'undefined') {
			this._appliedPaymentTotal = appliedPaymentTotal;
		}

		stats.push({ group, name: 'jobCostVersion', value: jobCost.itemVersion, changeOrderKey });
		stats.push({ group, name: 'appliedPaymentTotal', value: this.appliedPaymentTotal, changeOrderKey });
		stats.push({ group, name: 'builderDiscountPercent', value: this.builderDiscountPercent, changeOrderKey });
		stats.push({ group, name: 'builderUpgradeSellPrice', value: this.builderUpgradeSellPrice, changeOrderKey });
		stats.push({ group, name: 'clientUpgradeSellPrice', value: this.clientUpgradeSellPrice, changeOrderKey });
		stats.push({ group, name: 'orderSellPrice', value: this.orderSellPrice, changeOrderKey });
		stats.push({ group, name: 'orderTotalSellWithUpgrades', value: this.orderTotalSellWithUpgrades, changeOrderKey });
		stats.push({ group, name: 'orderTotalPrice', value: this.orderTotalPrice, changeOrderKey });
		stats.push({ group, name: 'subtradeTotalPrice', value: this.subtradeTotalPrice, changeOrderKey });
		stats.push({ group, name: 'solidSurfaceCounterTotalPrice', value: this.solidSurfaceCounterTotalPrice, changeOrderKey });
		stats.push({ group, name: 'grandTotalSell', value: this.grandTotalSell, changeOrderKey });
		stats.push({ group, name: 'grandTotalContract', value: this.grandTotalContract, changeOrderKey });

		forEach(stats, stat => {
			jobCost.stats.add(stat);
		});
	}
	toJSON () {
		return {
			jobCostVersion: this._jobCost.itemVersion,
			timestamp: Date.now(),
			appliedPaymentTotal: this.appliedPaymentTotal,
			orderCabinetCost: this.orderCabinetCost,
			orderCabinetSell: this.orderCabinetSell,
			orderCabinetFreightCost: this.orderCabinetFreightCost,
			orderOutsideSupplierCost: this.orderOutsideSupplierCost,
			orderOutsideSupplierSell: this.orderOutsideSupplierSell,
			orderWarehouseCost: this.orderWarehouseCost,
			orderWarehouseSell: this.orderWarehouseSell,
			orderMiscCost: this.orderMiscCost,
			orderMiscSell: this.orderMiscSell,
			orderAccessoryModCost: this.orderAccessoryModCost,
			orderAccessoryModSell: this.orderAccessoryModSell,
			orderGlassCost: this.orderGlassCost,
			orderGlassSell: this.orderGlassSell,
			orderSpecialGlassCost: this.orderSpecialGlassCost,
			orderSpecialGlassSell: this.orderSpecialGlassSell,
			orderInstallationCost: this.orderInstallationCost,
			orderInstallationSell: this.orderInstallationSell,
			orderInstallServiceCost: this.orderInstallServiceCost,
			orderInstallServiceSell: this.orderInstallServiceSell,
			orderReferralFeeCost: this.orderReferralFeeCost,
			orderReferralFeePostSubtotalCost: this.orderReferralFeePostSubtotalCost,
			orderDeliveryCost: this.orderDeliveryCost,
			orderDeliverySell: this.orderDeliverySell,
			builderUpgradeSellPrice: this.builderUpgradeSellPrice,
			clientUpgradeChargedToBuilderSellPrice: this.clientUpgradeChargedToBuilderSellPrice,
			clientUpgradeSellPrice: this.clientUpgradeSellPrice,
			clientUpgradeTaxesAndFees: this.clientUpgradeTaxesAndFees,
			orderTotalCost: this.orderTotalCost,
			orderCaculatedSellPrice: this.orderCaculatedSellPrice,
			orderSellPrice: this.orderSellPrice,
			orderTotalSellWithUpgrades: this.orderTotalSellWithUpgrades,
			orderNetPrice: this.orderNetPrice,
			orderTotalPrice: this.orderTotalPrice,
			orderWarrantyItems: this.orderWarrantyItems,
			orderTaxItems: this.orderTaxItems,
			orderCalculatedFeeItems: this.orderCalculatedFeeItems,
			clientUpgradeTaxItems: this.clientUpgradeTaxItems,
			subtradeCost: this.subtradeCost,
			subtradeSellPrice: this.subtradeSellPrice,
			subtradeTotalCost: this.subtradeTotalCost,
			subtradeTotalSellPrice: this.subtradeTotalSellPrice,
			subtradeTotalPrice: this.subtradeTotalPrice,
			subtradeWarrantyItems: this.subtradeWarrantyItems,
			subtradeTaxItems: this.subtradeTaxItems,
			solidSurfaceCounterCost: this.solidSurfaceCounterCost,
			solidSurfaceCounterSellPrice: this.solidSurfaceCounterSellPrice,
			solidSurfaceCounterTotalCost: this.solidSurfaceCounterTotalCost,
			solidSurfaceCounterTotalSellPrice: this.solidSurfaceCounterTotalSellPrice,
			solidSurfaceCounterTotalPrice: this.solidSurfaceCounterTotalPrice,
			solidSurfaceCounterWarrantyItems: this.solidSurfaceCounterWarrantyItems,
			solidSurfaceCounterTaxItems: this.solidSurfaceCounterTaxItems,
			grandTotalCost: this.grandTotalCost,
			grandTotalSell: this.grandTotalSell,
			grandTotalContract: this.grandTotalContract,
			extensionItems: this.extensionItems
		};
	}
}

export default JobCostSummary;